import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/**
 * Merges class names using clsx and tailwind-merge
 * @param inputs - Class values to merge
 * @returns Merged class string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a date to a relative time string (e.g., "5m ago")
 * @param date - Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(date: Date | string): string {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return `${diffSec}s ago`
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHour < 24) return `${diffHour}h ago`
  return `${diffDay}d ago`
}

/**
 * Formats a duration in seconds to a human-readable string
 * @param seconds - Duration in seconds
 * @returns Formatted duration string (e.g., "5m 30s")
 */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins < 60) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
  const hours = Math.floor(mins / 60)
  const remainingMins = mins % 60
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`
}

/**
 * Truncates a string to a maximum length with ellipsis
 * @param str - String to truncate
 * @param maxLength - Maximum length
 * @returns Truncated string
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 3) + "..."
}

/**
 * Parsed Azure DevOps URL components
 */
export interface AdoUrlParts {
  organization: string
  project: string
  repository: string
}

/**
 * Parses an Azure DevOps repository URL to extract organization, project, and repository name
 * @param url - Azure DevOps repository URL (e.g., https://dev.azure.com/org/project/_git/repo)
 * @returns Parsed URL parts or null if invalid
 * @example
 * parseAdoUrl('https://dev.azure.com/b-route/<your-org>%20API%20Definitions/_git/PoC')
 * // Returns: { organization: 'b-route', project: '<your-project>', repository: 'PoC' }
 */
export function parseAdoUrl(url: string): AdoUrlParts | null {
  try {
    const urlObj = new URL(url)
    
    // Expected format: https://dev.azure.com/{organization}/{project}/_git/{repository}
    // Or: https://{organization}.visualstudio.com/{project}/_git/{repository}
    
    const isDevAzure = urlObj.hostname === 'dev.azure.com'
    const isVisualStudio = urlObj.hostname.endsWith('.visualstudio.com')
    
    if (!isDevAzure && !isVisualStudio) {
      return null
    }
    
    const pathParts = urlObj.pathname.split('/').filter(p => p.length > 0)
    
    if (isDevAzure) {
      // Format: /organization/project/_git/repository
      if (pathParts.length < 4 || pathParts[2] !== '_git') {
        return null
      }
      
      return {
        organization: decodeURIComponent(pathParts[0]),
        project: decodeURIComponent(pathParts[1]),
        repository: decodeURIComponent(pathParts[3])
      }
    } else {
      // Format: /{project}/_git/{repository} (org is in subdomain)
      if (pathParts.length < 3 || pathParts[1] !== '_git') {
        return null
      }
      
      const organization = urlObj.hostname.split('.')[0]
      
      return {
        organization,
        project: decodeURIComponent(pathParts[0]),
        repository: decodeURIComponent(pathParts[2])
      }
    }
  } catch {
    return null
  }
}

/**
 * Token threshold colors for different usage levels (PER SESSION).
 * Based on Claude pricing (Sonnet 4.5: $3 input / $15 output per 1M tokens):
 * - LOW: < 150K tokens (~$0.45-1.00 per session) - green
 * - MEDIUM: 150K-300K tokens (~$1.00-2.50 per session) - yellow
 * - HIGH: > 300K tokens (>$2.50+ per session) - red
 */
export const TOKEN_THRESHOLD = {
  LOW: 150_000,    // < 150K = green (economical)
  MEDIUM: 300_000, // 150K-300K = yellow, > 300K = red (expensive)
} as const;

/**
 * Token threshold colors for AGGREGATE usage (across all sessions).
 * Much higher thresholds since this represents total platform usage.
 * - LOW: < 1M tokens - green (normal usage)
 * - MEDIUM: 1M-5M tokens - yellow (elevated usage)
 * - HIGH: > 5M tokens - red (high cost alert)
 */
export const AGGREGATE_TOKEN_THRESHOLD = {
  LOW: 1_000_000,    // < 1M = green
  MEDIUM: 5_000_000, // 1M-5M = yellow, > 5M = red
} as const;

/**
 * Formats a number into a compact representation (e.g., 1.2K, 3.4M).
 * @param n - Number to format
 * @returns Compact string representation
 * @example
 * formatCompactNumber(456) // "456"
 * formatCompactNumber(1234) // "1.2K"
 * formatCompactNumber(45678) // "46K"
 * formatCompactNumber(1234567) // "1.2M"
 */
export function formatCompactNumber(n: number): string {
  if (n < 1000) return n.toString();
  if (n < 1_000_000) {
    // Show one decimal for < 10K, no decimal for >= 10K
    return n < 10_000 
      ? `${(n / 1000).toFixed(1)}K`
      : `${Math.round(n / 1000)}K`;
  }
  return `${(n / 1_000_000).toFixed(1)}M`;
}

/**
 * Gets the Tailwind CSS color class for a token count based on thresholds.
 * - Green (< 100K): Low usage
 * - Yellow (100K-500K): Medium usage
 * - Red (> 500K): High usage
 * @param tokens - Number of tokens
 * @returns Tailwind CSS text color class
 */
export function getTokenThresholdColor(tokens: number): string {
  if (tokens < TOKEN_THRESHOLD.LOW) return 'text-green-600';
  if (tokens < TOKEN_THRESHOLD.MEDIUM) return 'text-yellow-600';
  return 'text-red-600';
}

/**
 * Gets the Tailwind CSS color class for AGGREGATE token count.
 * Uses higher thresholds for total platform usage.
 * @param tokens - Number of tokens (aggregate)
 * @returns Tailwind CSS text color class
 */
export function getAggregateTokenThresholdColor(tokens: number): string {
  if (tokens < AGGREGATE_TOKEN_THRESHOLD.LOW) return 'text-green-600';
  if (tokens < AGGREGATE_TOKEN_THRESHOLD.MEDIUM) return 'text-yellow-600';
  return 'text-red-600';
}

/**
 * Gets the background color variant for token threshold.
 * @param tokens - Number of tokens
 * @returns Tailwind CSS background color class
 */
export function getTokenThresholdBgColor(tokens: number): string {
  if (tokens < TOKEN_THRESHOLD.LOW) return 'bg-green-100';
  if (tokens < TOKEN_THRESHOLD.MEDIUM) return 'bg-yellow-100';
  return 'bg-red-100';
}

/**
 * Gets an accessibility label for the token threshold level.
 * @param tokens - Number of tokens
 * @returns Human-readable threshold level
 */
export function getTokenThresholdLabel(tokens: number): string {
  if (tokens < TOKEN_THRESHOLD.LOW) return 'low usage';
  if (tokens < TOKEN_THRESHOLD.MEDIUM) return 'medium usage';
  return 'high usage';
}
